/** The shape the web SDK rejects with, which the login screens read `code` and `description` from. */
export interface Auth0Error extends Error {
    code?: string;
    description?: string;
    statusCode?: number;
}

interface NativeRejection {
    code?: string;
}

// The shell's rejection code is "error=<auth0 error> status=<http status> detail=<description>".
const SHELL_CODE = /^error=(\S+) status=(\S+) detail=(.*)$/s;

const MAX_ATTEMPTS = /maximum number of attempts/i;

// The shell's /oauth/token reports a wrong code as `invalid_grant`; the web SDK reports
// `access_denied`, which is what AuthHeader matches.
function toWebCode(error: string, description: string | undefined): string {
    if (error !== 'invalid_grant') return error;
    return description && MAX_ATTEMPTS.test(description) ? 'too_many_attempts' : 'access_denied';
}

function present(value: unknown): string | undefined {
    return typeof value === 'string' && value !== '' && value !== 'null' ? value : undefined;
}

function parse(rejection: NativeRejection) {
    const match = rejection.code?.match(SHELL_CODE);
    if (!match) return undefined;
    return { error: present(match[1]), description: present(match[3]), status: match[2] };
}

export function toAuth0Error(cause: unknown): unknown {
    if (!cause || typeof cause !== 'object') return cause;
    const parsed = parse(cause as NativeRejection);
    if (!parsed?.error) return cause;
    const status = Number(parsed.status);
    return Object.assign(new Error(parsed.description ?? parsed.error, { cause }), {
        code: toWebCode(parsed.error, parsed.description),
        description: parsed.description,
        statusCode: Number.isFinite(status) && status > 0 ? status : undefined,
    }) satisfies Auth0Error;
}
